import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, User } from "@phosphor-icons/react";

const empty = { name: "", address: "", rooms: 1, notes: "", owner_name: "", owner_phone: "", owner_email: "" };

export default function Properties() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);

  const load = () => api.get("/properties").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form, rooms: parseInt(form.rooms) || 1 };
    try {
      if (editingId) await api.put(`/properties/${editingId}`, payload);
      else await api.post("/properties", payload);
      toast.success("OK");
      setOpen(false); setForm(empty); setEditingId(null);
      load();
    } catch (e) { toast.error("Error"); }
  };

  const del = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    await api.delete(`/properties/${id}`);
    load();
  };

  const edit = (p) => {
    setForm({ ...empty, ...p });
    setEditingId(p.id);
    setOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="label-eyebrow">{t("properties")}</div>
          <h1 className="font-heading text-4xl font-semibold tracking-tight text-slate-900">{t("properties")}</h1>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(empty); setEditingId(null); } }}>
          <DialogTrigger asChild>
            <Button data-testid="add-property-btn" className="bg-slate-900 hover:bg-slate-800 rounded-md">
              <Plus size={16} className="mr-2" /> {t("add")}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-xl">
            <DialogHeader><DialogTitle>{editingId ? t("edit") : t("add")}</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><label className="label-eyebrow">{t("property_name")}</label>
                <Input data-testid="property-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div className="col-span-2"><label className="label-eyebrow">{t("address")}</label>
                <Input data-testid="property-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
              <div><label className="label-eyebrow">{t("rooms")}</label>
                <Input data-testid="property-rooms" type="number" min={1} value={form.rooms} onChange={(e) => setForm({ ...form, rooms: e.target.value })} /></div>
              <div><label className="label-eyebrow">{t("owner_name")}</label>
                <Input data-testid="property-owner" value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} /></div>
              <div><label className="label-eyebrow">{t("owner_phone")}</label>
                <Input data-testid="property-owner-phone" value={form.owner_phone} onChange={(e) => setForm({ ...form, owner_phone: e.target.value })} /></div>
              <div><label className="label-eyebrow">{t("owner_email")}</label>
                <Input data-testid="property-owner-email" type="email" value={form.owner_email} onChange={(e) => setForm({ ...form, owner_email: e.target.value })} /></div>
              <div className="col-span-2"><label className="label-eyebrow">{t("notes")}</label>
                <Textarea data-testid="property-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              <DialogFooter className="col-span-2">
                <Button data-testid="property-save" type="submit" className="bg-slate-900 hover:bg-slate-800">{t("save")}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="properties-grid">
        {items.length === 0 && <div className="text-slate-500 text-sm">{t("no_data")}</div>}
        {items.map((p) => (
          <div key={p.id} className="stat-card bg-white border border-slate-200 rounded-lg p-6" data-testid={`property-${p.id}`}>
            <div className="font-heading text-xl font-medium text-slate-900">{p.name}</div>
            <div className="text-sm text-slate-500 mt-1">{p.address || "-"}</div>
            {p.owner_name && (
              <div className="mt-3 flex items-center gap-2 text-xs text-slate-600">
                <User size={14} weight="duotone" className="text-slate-400" />
                <span className="font-semibold">{p.owner_name}</span>
                {p.owner_phone && <span className="text-slate-400">• {p.owner_phone}</span>}
              </div>
            )}
            <div className="mt-4 flex items-center justify-between">
              <div className="text-xs text-slate-500">{p.rooms} {t("rooms")}</div>
              <div className="flex gap-2">
                <button onClick={() => edit(p)} className="p-2 hover:bg-slate-100 rounded-md" data-testid={`edit-${p.id}`}>
                  <PencilSimple size={16} />
                </button>
                <button onClick={() => del(p.id)} className="p-2 hover:bg-red-50 text-red-600 rounded-md" data-testid={`del-${p.id}`}>
                  <Trash size={16} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
